import { Divider } from "@msuite/ui/divider";
import { NumberInput, Select, SizeInput, Textarea, TextInput, WeightInput } from "@msuite/ui/form";
import { useTranslation } from "@msuite/ui/localization";
import type { LoadCarrierFormSchema } from "@msuite/utils/schemas";
import { useFormContext } from "react-hook-form";
import { useMentionOptions } from "@/hooks/use-mention-options";
import { useWarehouseZones } from "@/hooks/use-warehouse-zones";

/**
 * The fields of a load carrier. Headless, like every form in this directory — the modal
 * around it owns the submit.
 *
 * There is no number field and no group picker, which is the whole difference from
 * `ArticleForm`: a carrier is identified by what the yard calls it, unique per organisation, and
 * belongs to no catalogue above it. The balance is absent for a different reason — it follows the
 * carrier's ledger, so it is booked rather than typed.
 *
 * The home zone is the one field here that reaches outside the carrier. It is where these stand
 * when empty, and it is what gives a Leergut round somewhere to be carried from — such a round
 * has no warehouse zone of its own, so without it the boxes ship and nobody is asked to move
 * them. It stays optional, because that is a working state and not a broken one.
 */
export function LoadCarrierForm() {
	/** Context */
	const methods = useFormContext<LoadCarrierFormSchema>();
	const mentionOptions = useMentionOptions();

	/** Hooks */
	const { t } = useTranslation();

	/** Query */
	const { warehouseZones } = useWarehouseZones();

	/** Render */
	return (
		<div className="v-stack gap-4">
			<TextInput
				label={t("general.name")}
				isRequired
				control={methods.control}
				name="name"
				placeholder="Europalette"
			/>
			<Textarea
				disableFormatting
				label={t("general.description")}
				isRequired={false}
				control={methods.control}
				name="description"
				{...mentionOptions}
			/>
			<Select
				control={methods.control}
				name="homeZoneId"
				isRequired={false}
				label={t("stock.home_zone")}
				helperText={t("stock.home_zone_description")}
				options={warehouseZones.map((warehouseZone) => ({
					label: warehouseZone.name,
					value: warehouseZone.id,
					item: warehouseZone,
				}))}
			/>
			<Divider />
			<SizeInput />
			<WeightInput />
			<Divider />
			<NumberInput
				label={t("stock.warning_stock_balance")}
				isRequired
				control={methods.control}
				name="warningStockBalance"
				variant="integer"
				min={0}
				step={1}
			/>
			<NumberInput
				label={t("stock.minimum_stock_balance")}
				isRequired
				control={methods.control}
				name="minimumStockBalance"
				variant="integer"
				min={0}
				step={1}
			/>
		</div>
	);
}
